// TODO: Remove when the listhelpers variable is being used
/* eslint-disable @typescript-eslint/no-unused-vars */

import {cloneElement, useContext, useMemo} from 'react';

import {FieldArray, Form, Formik} from 'formik';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import PaidIcon from '@mui/icons-material/Paid';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SpaIcon from '@mui/icons-material/Spa';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from '@mui/material';

import {CompanyProfileDialogButtons} from '../CompanyProfileDialogButtons';
import {
  BENEFIT_CHECK_COLOR,
  BENEFIT_CHECK_UNCHECKED_COLOR,
  getBenefitCardHeaderSx,
  getBenefitCardSx,
} from '../profileBenefitStyles';

import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {CompanyProfile, Benefit} from '../../types';

type CompanyProfileBenefitsFormProps = {
  company: CompanyProfile;
  onCancel(): void;
  onSubmit(company: CompanyProfile): void;
};

export const CompanyProfileBenefitsForm = ({company, onCancel, onSubmit}: CompanyProfileBenefitsFormProps) => {
  const currentLanguage = useContext(LanguageContext);

  const handleCancel = () => {
    onCancel();
  };

  const categoryIcons: {[key: string]: JSX.Element} = {
    happiness: <EmojiEmotionsIcon />,
    office: <CorporateFareIcon />,
    health: <SpaIcon />,
    financial: <PaidIcon />,
  };

  const handleBenefitChange = (
    benefit: any,
    event: {target: {checked: any}},
    benefitIndex: any,
    benefitHelpers: any
  ) => {
    const benefitToChange = benefit;
    benefitToChange.selectedAt = event.target.checked ? new Date().toISOString() : null;

    benefitHelpers.replace(benefitIndex, benefitToChange);
  };

  const [benefitsByCategory, categoryLabels] = useMemo(() => {
    const categorized: Record<string, Array<Benefit>> = {};
    const categoryLabels: Record<string, Pick<Benefit['category'], 'labels'>> = {};

    company.benefits?.map((benefit) => {
      categoryLabels[benefit.category.categoryKey] = benefit.category;

      if (categorized[benefit.category.categoryKey]) {
        categorized[benefit.category.categoryKey].push(benefit);
      } else {
        categorized[benefit.category.categoryKey] = [benefit];
      }
    });

    return [categorized, categoryLabels];
  }, [company.benefits]);

  const onFormikSubmit = async (value: any) => {
    const updatedData = {
      // we need to convert from object to a simple list of benefits as the backend requires it
      benefits: Object.values(value.benefitsByCategory).flatMap((e) => e) as Benefit[],
    };

    onSubmit({...company, ...updatedData});
  };

  // Filters benefits that have a value in the label of the current language
  const withLabels = (list: Benefit[]) => list.filter((benefit) => benefit.labels?.[currentLanguage]);

  return (
    <Formik initialValues={{benefitsByCategory}} onSubmit={onFormikSubmit}>
      {(formik) => (
        <Form>
          <FieldArray name='benefits'>
            {(_listHelpers) => (
              // The listHelpers will stay in for now, because it will be used for the textfield feature.
              <>
                <Grid container spacing={2} mb={4}>
                  {Object.keys(formik.values?.benefitsByCategory).map((category, categoryIndex) => (
                    <Grid item xs={12} sm={3} key={categoryIndex}>
                      <Card sx={getBenefitCardSx()}>
                        <CardHeader
                          sx={getBenefitCardHeaderSx()}
                          title={
                            <Typography variant='h6' textTransform={'capitalize'} color={'white'}>
                              {categoryLabels[category]?.labels?.[currentLanguage]}
                            </Typography>
                          }
                          avatar={cloneElement(categoryIcons[category], {
                            sx: {
                              fontSize: '32px',
                              color: 'white',
                            },
                          })}
                        />
                        <CardContent sx={{flex: 1}}>
                          <FieldArray name='benefits'>
                            {(benefitHelpers) => (
                              <>
                                {withLabels(formik.values?.benefitsByCategory[category]).map(
                                  (benefit, benefitIndex) => (
                                    <FormGroup key={benefitIndex}>
                                      <FormControlLabel
                                        sx={{alignItems: 'flex-start'}}
                                        onChange={formik.handleChange}
                                        name={benefit.benefitKey}
                                        control={
                                          <Checkbox
                                            onChange={(event) => {
                                              handleBenefitChange(benefit, event, benefitIndex, benefitHelpers);
                                            }}
                                            icon={
                                              <RadioButtonUncheckedIcon
                                                sx={{
                                                  color: BENEFIT_CHECK_UNCHECKED_COLOR,
                                                }}
                                              />
                                            }
                                            checkedIcon={
                                              <CheckCircleIcon
                                                sx={{
                                                  color: BENEFIT_CHECK_COLOR,
                                                }}
                                              />
                                            }
                                            checked={
                                              !!formik.values?.benefitsByCategory[category][benefitIndex].selectedAt
                                            }
                                          />
                                        }
                                        label={
                                          <Typography
                                            variant='body1'
                                            sx={{
                                              overflowWrap: 'anywhere',
                                              marginTop: '6px',
                                            }}
                                          >
                                            {benefit.labels?.[currentLanguage]}
                                          </Typography>
                                        }
                                      />
                                    </FormGroup>
                                  )
                                )}
                              </>
                            )}
                          </FieldArray>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </FieldArray>
          {/* Buttons save & cancel */}
          <Grid item container justifyContent={'flex-end'}>
            <CompanyProfileDialogButtons onCancel={handleCancel} />
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
